import React from "react";

import { QuoteBreakdown } from "@/shared/components/payments/QuoteBreakdown";
import { useQuoteCountdown } from "@/shared/hooks/useQuoteCountdown";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { NativeQuote } from "../../services/paymentsQuotes.service";
import type { EnhancedCountry } from "../../types/dispersions-api.types";

export interface QuoteSummaryCardProps {
  quote: NativeQuote;
  dest: EnhancedCountry | null;
}

/**
 * Step 1 "quote ready" card: the countdown header plus the shared money breakdown. The 1-second tick is
 * INTENTIONALLY confined here so it re-renders this card only, never the whole step (selects, balance card,
 * amount inputs) — which is why the countdown is a header slot rather than part of the breakdown.
 */
export const QuoteSummaryCard = React.memo(function QuoteSummaryCard({ quote, dest }: QuoteSummaryCardProps) {
  const { t } = useTranslations("dispersions");
  const { formattedTime, isExpired, timeRemaining } = useQuoteCountdown(quote.expires_at ?? null);

  // Info (blue) → warning (amber) at ≤1 min → error (red) once expired,
  // as in the design prototype.
  let barClass = "bg-muted text-foreground";
  if (!isExpired && timeRemaining <= 60_000) {
    barClass = "bg-warning-container text-warning-container-foreground";
  }
  if (isExpired) {
    barClass = "bg-danger-container text-danger-container-foreground";
  }

  const header = (
    <div className={cn("flex items-center gap-2 px-5 py-2.5 text-sm font-medium", barClass)}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <span>{t("quote_rate_valid_for")}</span>
      <span className="font-bold tabular-nums">{formattedTime}</span>
    </div>
  );

  return (
    <QuoteBreakdown
      quote={quote}
      header={header}
      serviceFeeSuffix={dest?.name}
      labels={{
        send: t("step1_origin_label"),
        conversionFee: t("quote_conversion_fee_label"),
        rate: t("quote_exchange_rate_label"),
        converted: t("quote_converted_amount_label"),
        serviceFee: t("quote_dispersion_fee_label"),
        totalDebit: t("quote_total_debit_label"),
        receives: t("quote_recipient_receives_label"),
      }}
    />
  );
});
